import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { useTenantStore } from '@/stores/tenantStore';
import { authApi, ssoApi } from '@/api/endpoints';
import { deriveMainRole, isDeveloperRole } from '@/auth/permissions';
import type { AuthUser, LoginRequest, LoginResponse, UserRole } from '@/types';

const ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

/** Decode JWT payload without a library. */
function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64));
}

/** Extract AuthUser from JWT claims. Reads the FULL role-claim array. */
function extractUser(token: string): AuthUser {
  const claims = decodeJwtPayload(token);
  const rawRoles = claims[ROLE_CLAIM];
  const roles: string[] = Array.isArray(rawRoles)
    ? (rawRoles as unknown[]).map(String)
    : rawRoles != null
      ? [String(rawRoles)]
      : [];

  const mainRole = deriveMainRole(roles);
  const isDeveloper = isDeveloperRole(roles);
  // Legacy bridge: the axiosClient tenant interceptor still keys off `role`.
  const role: UserRole = mainRole === 'platformAdmin' ? 'PlatformAdmin' : 'TenantAdmin';

  return {
    userId: (claims.sub as string) ?? '',
    email: (claims.email as string) ?? '',
    displayName: (claims.displayName as string) ?? (claims.email as string) ?? '',
    role,
    roles,
    mainRole,
    isDeveloper,
    tenantId: (claims.tenantId as string) ?? '',
  };
}

/**
 * Provides login / logout actions wired to the auth store and router.
 */
export function useAuth() {
  const navigate = useNavigate();
  const { login: storeLogin, logout: storeLogout, user, isAuthenticated } = useAuthStore();

  /** Shared post-token flow: extract user from the issued token, persist, go home.
   *  Reached after a successful password login OR a successful forced password
   *  change (both return a full token set). The backend issues a token ONLY for a
   *  ready account — a must-reset account is rejected at /login with 403 (handled
   *  in `login` below), so there is no "logged in but must reset" state to route. */
  const completeAuth = useCallback(
    (data: LoginResponse) => {
      const userInfo = extractUser(data.accessToken);
      storeLogin(data.accessToken, data.refreshToken, userInfo);
      navigate('/');
    },
    [storeLogin, navigate],
  );

  const login = useCallback(
    async (credentials: LoginRequest) => {
      try {
        const { data } = await authApi.login(credentials);
        completeAuth(data);
      } catch (err) {
        // Valid credentials on a temp-password account → backend returns
        // 403 { error: "password_reset_required" } with NO token. Route to the
        // (pre-auth) change-password page carrying the typed credentials so the
        // user can set a new password and get a session. Any other error bubbles
        // up to the LoginPage error banner.
        const data = axios.isAxiosError(err) ? err.response?.data : undefined;
        if (
          axios.isAxiosError(err) &&
          err.response?.status === 403 &&
          (data as { error?: string } | undefined)?.error === 'password_reset_required'
        ) {
          // Carry the RESOLVED canonical tenantId from the response (OP-221) — the typed value may be a
          // slug/name, which the gateway tenant gate can't resolve for the change-password call (403).
          const resolvedTenantId =
            (data as { tenantId?: string } | undefined)?.tenantId || credentials.tenantId;
          navigate('/change-password', {
            state: {
              email: credentials.email,
              currentPassword: credentials.password,
              tenantId: resolvedTenantId,
            },
          });
          return;
        }
        throw err;
      }
    },
    [completeAuth, navigate],
  );

  /** Complete a marketing-site sign-in hand-off (PRE-auth): redeem the single-use code, then
   *  establish the returned session exactly as a password login does. Rejects on any refusal. */
  const completeHandoff = useCallback(
    async (code: string, tenantId: string) => {
      const { data } = await authApi.redeemHandoff(code, tenantId);
      completeAuth(data);
    },
    [completeAuth],
  );

  /** Complete a forced first-login password change (PRE-auth). Sets the new
   *  password via the anonymous change-password endpoint (tenantId → X-Tenant-Id
   *  header, since no session exists yet), then establishes the returned session. */
  const changePassword = useCallback(
    async (email: string, currentPassword: string, newPassword: string, tenantId: string) => {
      const { data } = await ssoApi.changePassword(email, currentPassword, newPassword, tenantId);
      completeAuth(data);
    },
    [completeAuth],
  );

  // Microsoft SSO removed 2026-06-09 (deferred). `loginWithSso` (Entra id_token
  // exchange) lived here. Re-enable per Output/Runbooks/admin-portal-microsoft-sso-reenable.md.

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Best-effort — clear local state even if server call fails
    }
    storeLogout();
    useTenantStore.getState().clear();
    navigate('/login');
  }, [storeLogout, navigate]);

  return { login, changePassword, completeHandoff, logout, user, isAuthenticated };
}
